/**
 * UploadTab Component
 * File upload interface with queue management
 */

import React from 'react';
import { Stack, Group, Center } from '@/components/ui/layout';
import { Button, Text } from '@/components/ui/core';
import { Select } from '@/components/ui/inputs';
import { Card } from '@/components/ui/data-display';
import { UploadQueueItem } from '@/components/ccms-components/file-management';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconFile, IconX } from '@tabler/icons-react';
import { useFileUpload } from '../hooks/useFileUpload';
import { FileDomain } from '../types';

interface UploadTabProps {
  domain?: string;
  recordId?: string;
  folderPath?: string;
  onUploadComplete?: () => void;
}

export const UploadTab: React.FC<UploadTabProps> = ({
  domain,
  recordId,
  folderPath = '/',
  onUploadComplete,
}) => {
  const upload = useFileUpload({
    domain,
    recordId,
    folderPath,
    maxSize: 500 * 1024 * 1024, // 500MB
  });

  const domains: FileDomain[] = ['User', 'Member', 'Event', 'News', 'Procurement', 'Temp'];

  const handleDrop = (files: File[]) => {
    upload.addToQueue(files);
  };

  const handleUploadAll = async () => {
    await upload.uploadAll();
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  return (
    <Stack gap="md">
      {/* Configuration */}
      <Card p="md" variant="outlined">
        <Stack gap="sm">
          <Text fw={600}>Upload Configuration</Text>
          <Group grow>
            <Select
              label="Domain"
              placeholder="Select domain"
              data={domains}
              value={upload.config.domain || ''}
              onChange={(value) => upload.setConfig({ domain: value || undefined })}
            />
            <Text size="sm" c="dimmed" mt="xl">
              Folder: {upload.config.folderPath || '/'}
            </Text>
          </Group>
        </Stack>
      </Card>

      {/* File Uploader */}
      <Dropzone
        onDrop={handleDrop}
        maxSize={upload.config.maxSize}
        multiple
        disabled={upload.uploading}
      >
        <Group justify="center" gap="xl" mih={220} style={{ pointerEvents: 'none' }}>
          <Dropzone.Accept>
            <IconUpload size={52} stroke={1.5} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX size={52} stroke={1.5} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconFile size={52} stroke={1.5} />
          </Dropzone.Idle>

          <div>
            <Text size="xl" inline>
              Drag files here or click to select
            </Text>
            <Text size="sm" c="dimmed" inline mt={7}>
              Maximum file size: 500MB
            </Text>
          </div>
        </Group>
      </Dropzone>

      {/* Upload Queue */}
      {upload.queue.length > 0 && (
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>Upload Queue ({upload.queue.length} files)</Text>
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                onClick={handleUploadAll}
                disabled={upload.uploading || upload.queue.filter(q => q.status === 'pending').length === 0}
                loading={upload.uploading}
              >
                Upload All
              </Button>
              <Button
                size="xs"
                variant="subtle"
                onClick={upload.clearCompleted}
                disabled={upload.queue.filter(q => q.status === 'completed').length === 0}
              >
                Clear Completed
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="red"
                onClick={upload.clearQueue}
              >
                Clear All
              </Button>
            </Group>
          </Group>

          <Stack gap="xs">
            {upload.queue.map((item) => (
              <UploadQueueItem
                key={item.id}
                file={{
                  id: item.id,
                  fileName: item.fileName,
                  fileSize: item.fileSize,
                  mimeType: item.file?.type || 'application/octet-stream',
                  progress: item.progress,
                  status: item.status === 'cancelled' ? 'failed' : item.status,
                }}
                onCancel={() => upload.cancelUpload(item.id)}
                onRetry={() => upload.retryUpload(item.id)}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* Summary */}
      {(upload.completedCount > 0 || upload.failedCount > 0) && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Completed: {upload.completedCount} | Failed: {upload.failedCount}
          </Text>
        </Group>
      )}
    </Stack>
  );
};
